"use client"

import { useCallback, useEffect } from "react"
import { Building2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { PageContainer } from "@/components/ui/page-container"
import { PageHeader } from "@/components/ui/page-header"
import { PageHeaderLeading } from "@/components/navigation/page-header-leading"
import { OrganisationProfileDetailSkeleton } from "@/components/layout/entity-detail-skeleton"
import { OrganisationProfileView } from "@/components/organisation-profile"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import { useArtifactCanvas } from "@/components/artifact/artifact-canvas-context"
import { EMPLOYER_ORGANISATION_HUB_PATH, getPageHeader } from "@/constants"
import { EMPLOYER_ORGANISATION_NEW_SETUP_PATH } from "@/lib/employer-org-setup"

export function EmployerOrganisationHubPage() {
  const ctx = useEmployerOrganisationOptional()
  const navigate = useNavigate()
  const { closeCanvas } = useArtifactCanvas()

  useEffect(() => () => closeCanvas(), [closeCanvas])

  const pageHeader = getPageHeader(
    EMPLOYER_ORGANISATION_HUB_PATH,
    "Organisation",
    "View and edit your active organisation.",
  )

  const openCreate = useCallback(() => {
    navigate(EMPLOYER_ORGANISATION_NEW_SETUP_PATH)
  }, [navigate])

  if (!ctx?.enabled) {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading />} {...pageHeader} icon={<Building2 className="h-5 w-5" />} />
        <p className="text-sm text-muted-foreground">Switch to employer focus to manage your organisation.</p>
      </PageContainer>
    )
  }

  if (ctx.organisationsLoading) {
    return <OrganisationProfileDetailSkeleton />
  }

  const slug = ctx.activeOrganisationSlug?.trim() ?? ""

  if (!slug && ctx.organisations.length === 0) {
    return <OrganisationProfileView onCreate={openCreate} />
  }

  if (!slug) {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading />} {...pageHeader} icon={<Building2 className="h-5 w-5" />} />
        <p className="text-sm text-muted-foreground">Choose a workspace from the sidebar switcher.</p>
      </PageContainer>
    )
  }

  return <OrganisationProfileView slug={slug} onCreate={openCreate} />
}
